// renderer.js — WebGPU 3DGS 光柵化器（與 decoder 共用 device，高斯資料留在 GPU）。
// 流程/幀：depthKey(compute) → scan(compute) → scatter(compute, GPU counting sort) → splat render pass。
// 高斯記錄 = 17 floats（ply-ready）：[0..2]xyz [6..8]f_dc [9]opacity_logit [10..12]scale_log [13..16]quat(w,x,y,z)
const BINS = 65536;

// 高斯記錄存取器：f32 = array<f32>（17/顆）；f16 = array<u32>（9/顆，unpack2x16float）
const ACC = (f16) => f16
  ? `@group(0) @binding(1) var<storage, read> gauss: array<u32>;
fn gf(g:u32,k:u32)->f32 { let u=gauss[g*9u+(k>>1u)]; let p=unpack2x16float(u); return select(p.x, p.y, (k&1u)==1u); }`
  : `@group(0) @binding(1) var<storage, read> gauss: array<f32>;
fn gf(g:u32,k:u32)->f32 { return gauss[g*17u+k]; }`;

const depthWGSL = (f16) => `
struct Cam { view:mat4x4<f32>, proj:mat4x4<f32>, campos:vec4<f32>, vp:vec4<f32>, nf:vec4<f32>, cnt:vec4<u32> };
@group(0) @binding(0) var<uniform> cam: Cam;
${ACC(f16)}
@group(0) @binding(2) var<storage, read_write> keys: array<u32>;
@group(0) @binding(3) var<storage, read_write> hist: array<atomic<u32>>;
@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x; if (i >= cam.cnt.x) { return; }
  let vz = (cam.view * vec4<f32>(gf(i,0u), gf(i,1u), gf(i,2u), 1.0)).z;  // 相機空間 z（看向 -z）
  let depth = -vz;
  let tk = clamp((depth - cam.nf.x) / (cam.nf.y - cam.nf.x), 0.0, 1.0);
  let key = 65535u - u32(tk * 65535.0);   // 遠的 key 小 → 升序 = 遠到近
  keys[i] = key;
  atomicAdd(&hist[key], 1u);
}`;

const WGSL_SCAN = `
@group(0) @binding(0) var<storage, read> hist: array<u32>;
@group(0) @binding(1) var<storage, read_write> offs: array<u32>;
@compute @workgroup_size(1)
fn main() {
  var acc = 0u;
  for (var k=0u; k<${BINS}u; k=k+1u) { offs[k] = acc; acc = acc + hist[k]; }
}`;

const WGSL_SCATTER = `
struct Cam { view:mat4x4<f32>, proj:mat4x4<f32>, campos:vec4<f32>, vp:vec4<f32>, nf:vec4<f32>, cnt:vec4<u32> };
@group(0) @binding(0) var<uniform> cam: Cam;
@group(0) @binding(1) var<storage, read> keys: array<u32>;
@group(0) @binding(2) var<storage, read_write> offs: array<atomic<u32>>;
@group(0) @binding(3) var<storage, read_write> sortedIdx: array<u32>;
@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x; if (i >= cam.cnt.x) { return; }
  let pos = atomicAdd(&offs[keys[i]], 1u);
  sortedIdx[pos] = i;
}`;

const renderWGSL = (f16) => `
struct Cam { view:mat4x4<f32>, proj:mat4x4<f32>, campos:vec4<f32>, vp:vec4<f32>, nf:vec4<f32>, cnt:vec4<u32> };
@group(0) @binding(0) var<uniform> cam: Cam;
${ACC(f16)}
@group(0) @binding(2) var<storage, read> sortedIdx: array<u32>;

struct VOut { @builtin(position) pos: vec4<f32>, @location(0) col: vec4<f32>, @location(1) xy: vec2<f32> };

fn quatMat(q: vec4<f32>) -> mat3x3<f32> {
  let w=q.x; let x=q.y; let y=q.z; let z=q.w;   // ply rot = (w,x,y,z)；標準 R（column-major）
  return mat3x3<f32>(
    1.0-2.0*(y*y+z*z), 2.0*(x*y+w*z),     2.0*(x*z-w*y),
    2.0*(x*y-w*z),     1.0-2.0*(x*x+z*z), 2.0*(y*z+w*x),
    2.0*(x*z+w*y),     2.0*(y*z-w*x),     1.0-2.0*(x*x+y*y));
}

@vertex
fn vs(@builtin(vertex_index) vi: u32, @builtin(instance_index) ii: u32) -> VOut {
  let gi = sortedIdx[ii];
  let center = vec3<f32>(gf(gi,0u), gf(gi,1u), gf(gi,2u));
  let scale = exp(vec3<f32>(gf(gi,10u), gf(gi,11u), gf(gi,12u)));
  let q = normalize(vec4<f32>(gf(gi,13u), gf(gi,14u), gf(gi,15u), gf(gi,16u)));
  let opacity = 1.0/(1.0+exp(-gf(gi,9u)));
  let rgb = vec3<f32>(gf(gi,6u), gf(gi,7u), gf(gi,8u)) * 0.28209479177387814 + 0.5;

  // 標準 EWA：Σ_world = R·diag(s²)·Rᵀ → Σ_cam = Rv·Σ·Rvᵀ → Σ_2d = J·Σ_cam·Jᵀ
  let R = quatMat(q);
  let Sd = mat3x3<f32>(scale.x*scale.x,0.0,0.0, 0.0,scale.y*scale.y,0.0, 0.0,0.0,scale.z*scale.z);
  let Sigma = R * Sd * transpose(R);
  let t = cam.view * vec4<f32>(center, 1.0);                                  // 相機空間
  let Rv = mat3x3<f32>(cam.view[0].xyz, cam.view[1].xyz, cam.view[2].xyz);    // world→cam 旋轉
  let Scam = Rv * Sigma * transpose(Rv);
  let focal = vec2<f32>(cam.proj[0][0]*cam.vp.x*0.5, cam.proj[1][1]*cam.vp.y*0.5);
  // J（2x3，row2=0）column-major：col0=(fx/z,0,0) col1=(0,fy/z,0) col2=(-fx·x/z²,-fy·y/z²,0)
  let J = mat3x3<f32>(focal.x/t.z, 0.0, 0.0,
                      0.0, focal.y/t.z, 0.0,
                      -(focal.x*t.x)/(t.z*t.z), -(focal.y*t.y)/(t.z*t.z), 0.0);
  let cov = J * Scam * transpose(J);
  var c0 = cov[0][0] + 0.3; let c1 = cov[0][1]; var c2 = cov[1][1] + 0.3;

  let mid = 0.5*(c0+c2);
  let det = c0*c2 - c1*c1;
  let rad = sqrt(max(0.0, mid*mid - det));   // 合法協方差 rad≤mid → lam2≥0，不會 NaN
  let lam1 = mid + rad;
  let lam2 = max(mid - rad, 0.0);
  let dir = normalize(vec2<f32>(c1, lam1 - c0) + vec2<f32>(1e-6, 0.0));
  let majorAxis = min(sqrt(2.0*lam1), 1024.0) * dir;
  let minorAxis = min(sqrt(2.0*lam2), 1024.0) * vec2<f32>(dir.y, -dir.x);

  // quad 角（triangle-strip 4 點）
  var corner = vec2<f32>(-2.0,-2.0);
  if (vi==1u) { corner = vec2<f32>(2.0,-2.0); }
  else if (vi==2u) { corner = vec2<f32>(-2.0,2.0); }
  else if (vi==3u) { corner = vec2<f32>(2.0,2.0); }

  let clip = cam.proj * t;
  if (clip.w <= 0.0) { var o: VOut; o.pos = vec4<f32>(0.0,0.0,2.0,1.0); o.col=vec4<f32>(0.0); o.xy=vec2<f32>(0.0); return o; }
  let offset = (corner.x*majorAxis + corner.y*minorAxis) / cam.vp.xy * 2.0;
  var o: VOut;
  o.pos = vec4<f32>(clip.xy/clip.w + offset, clip.z/clip.w, 1.0);
  o.col = vec4<f32>(rgb, opacity);
  o.xy = corner;
  return o;
}

@fragment
fn fs(in: VOut) -> @location(0) vec4<f32> {
  let A = -dot(in.xy, in.xy);
  if (A < -4.0) { discard; }
  let a = exp(A) * in.col.a;
  return vec4<f32>(in.col.rgb * a, a);   // premultiplied
}`;

export function createRenderer(device, canvas, maxCount) {
  const ctx = canvas.getContext('webgpu');
  const format = navigator.gpu.getPreferredCanvasFormat();
  ctx.configure({ device, format, alphaMode: 'premultiplied' });

  const ST = GPUBufferUsage.STORAGE, CD = GPUBufferUsage.COPY_DST;
  const keys = device.createBuffer({ size: maxCount * 4, usage: ST });
  const sortedIdx = device.createBuffer({ size: maxCount * 4, usage: ST });
  const hist = device.createBuffer({ size: BINS * 4, usage: ST | CD | GPUBufferUsage.COPY_SRC });
  const offs = device.createBuffer({ size: BINS * 4, usage: ST | CD });
  const CAMSZ = 192; // view(64)+proj(64)+campos+vp+nf+cnt(4×16)
  const cam = device.createBuffer({ size: CAMSZ, usage: GPUBufferUsage.UNIFORM | CD });
  const zeros = new Uint32Array(BINS);

  const mkPipe = (code, ep) => device.createComputePipeline({ layout: 'auto', compute: { module: device.createShaderModule({ code }), entryPoint: ep || 'main' } });
  const pScan = mkPipe(WGSL_SCAN), pScatter = mkPipe(WGSL_SCATTER);
  // f32 / f16 各一組 depth + render pipeline
  const pDepthF = { false: mkPipe(depthWGSL(false)), true: mkPipe(depthWGSL(true)) };
  const mkRender = (f16) => { const m = device.createShaderModule({ code: renderWGSL(f16) }); return device.createRenderPipeline({ layout: 'auto',
    vertex: { module: m, entryPoint: 'vs' },
    fragment: { module: m, entryPoint: 'fs', targets: [{ format, blend: {
      color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
      alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' } } }] },
    primitive: { topology: 'triangle-strip' } }); };
  const pRenderF = { false: mkRender(false), true: mkRender(true) };

  let frame = null, frameCount = 0, f16 = false, pDepth, pRender;
  function setFrame(buffer, count, isF16) { frame = buffer; frameCount = Math.min(count, maxCount); f16 = !!isF16; pDepth = pDepthF[f16]; pRender = pRenderF[f16]; rebind(); }

  let bgDepth, bgScan, bgScatter, bgRender;
  function rebind() {
    if (!frame) return;
    bgDepth = device.createBindGroup({ layout: pDepth.getBindGroupLayout(0), entries: [
      {binding:0,resource:{buffer:cam}},{binding:1,resource:{buffer:frame}},{binding:2,resource:{buffer:keys}},{binding:3,resource:{buffer:hist}}]});
    bgScatter = device.createBindGroup({ layout: pScatter.getBindGroupLayout(0), entries: [
      {binding:0,resource:{buffer:cam}},{binding:1,resource:{buffer:keys}},{binding:2,resource:{buffer:offs}},{binding:3,resource:{buffer:sortedIdx}}]});
    bgRender = device.createBindGroup({ layout: pRender.getBindGroupLayout(0), entries: [
      {binding:0,resource:{buffer:cam}},{binding:1,resource:{buffer:frame}},{binding:2,resource:{buffer:sortedIdx}}]});
  }
  bgScan = device.createBindGroup({ layout: pScan.getBindGroupLayout(0), entries: [
    {binding:0,resource:{buffer:hist}},{binding:1,resource:{buffer:offs}}]});

  function render(view, proj, campos, near, far) {
    if (!frame || frameCount === 0) return;
    const w = canvas.width, h = canvas.height;
    const ab = new ArrayBuffer(CAMSZ);
    const f32 = new Float32Array(ab);
    f32.set(view, 0); f32.set(proj, 16);
    f32.set([campos[0], campos[1], campos[2], 0], 32);
    f32.set([w, h, 0, 0], 36);
    f32.set([near, far, 0, 0], 40);
    new Uint32Array(ab, 44*4, 4).set([frameCount, 0, 0, 0]);
    device.queue.writeBuffer(cam, 0, ab);
    device.queue.writeBuffer(hist, 0, zeros);

    const enc = device.createCommandEncoder();
    let p = enc.beginComputePass(); p.setPipeline(pDepth); p.setBindGroup(0, bgDepth); p.dispatchWorkgroups(Math.ceil(frameCount/256)); p.end();
    p = enc.beginComputePass(); p.setPipeline(pScan); p.setBindGroup(0, bgScan); p.dispatchWorkgroups(1); p.end();
    p = enc.beginComputePass(); p.setPipeline(pScatter); p.setBindGroup(0, bgScatter); p.dispatchWorkgroups(Math.ceil(frameCount/256)); p.end();
    const rp = enc.beginRenderPass({ colorAttachments: [{ view: ctx.getCurrentTexture().createView(),
      clearValue: { r:0, g:0, b:0, a:1 }, loadOp: 'clear', storeOp: 'store' }] });
    rp.setPipeline(pRender); rp.setBindGroup(0, bgRender); rp.draw(4, frameCount); rp.end();
    device.queue.submit([enc.finish()]);
  }

  return { setFrame, render };
}
